import React from 'react';
import Icon from '@material-ui/core/Icon';
import Button from '@material-ui/core/Button';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { withTranslation, WithTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { Link as RouterLink } from 'react-router-dom';
// Components
import App from '../../components/app';
import ButtonTo from '../../components/buttonTo';
import { Dialog } from '../../components/dialog';
import EnhancedTable from '../../components/table';
import { TooltipButton } from '../../components/tooltip-button';
import ReserverForbiddenView from './reserver-forbidden';
import ErrorMessage from '../../components/error-message/error-message';
// Modules
import {
    BookingStatus,
    DispatchGroup,
    RiderReserver,
    ReservationStatus,
    BookingCancelPayload,
} from '../../redux/reducers/api-types';
import { selectors as riderSelectors } from '../../redux/reducers/riders';
import { tableRequestPoints } from '../../redux/reducers/table/module';
import { actions as reservationActions } from '../../redux/reducers/reservations/module';
import { actions as bookingActions } from '../../redux/reducers/bookings/module';
import { actions as riderActions } from '../../redux/reducers/riders/module';
import { ErrorPayload, selectors as errorSelectors } from '../../redux/reducers/errors';
import { actions as driverActions, selectors as driverSelectors } from '../../redux/reducers/drivers';
// Ressources
import { paths } from '../paths';
import withGlobalStyles from '../../utils/global-styles';
import UserUtils, { AccountRight, RightRank } from '../../utils/userUtils';
import { generateOptionsString, getDispatchGroupOptions } from '../../utils/businessUtils';
import StringUtils from '../../utils/stringUtils';
import { clsx } from '../../utils/htmlUtils';
import { FormatType, HeadCell, PickerOption } from '../../local-types';
import envConfig from '../../config';

interface ReserverViewProps extends WithTranslation {
    globalClasses: any;
    cancelReservationError: ErrorPayload;
    cancelReservationCallCenterError: ErrorPayload;
    cancelBookingCallCenterError: ErrorPayload;
    impersonateUserError: ErrorPayload;
    foundRider: RiderReserver;
    dispatchGroups: DispatchGroup[];
    getDispatchGroups: () => void;
    cancelReservation: (id: string, payload: BookingCancelPayload, callback: Function) => void;
    cancelReservationCallCenter: (payload: any, callback: Function) => void;
    cancelBookingCallCenter: (payload: any, callback: Function) => void;
    impersonateUser: (id: string, callback: React.EffectCallback) => void;
}

const ReserverViewPage = (props: ReserverViewProps): JSX.Element => {
    const classes = useStyles();

    const {
        t,
        globalClasses,
        foundRider,
        dispatchGroups,
        cancelReservationError,
        cancelReservationCallCenterError,
        cancelBookingCallCenterError,
        getDispatchGroups,
        impersonateUser,
    } = props;

    if (dispatchGroups.length === 0) {
        getDispatchGroups();
    }

    const tableRef = React.useRef(null);
    const tableBookingRef = React.useRef(null);

    // Usage for booking status options
    const generateBookingStatusOptions = (): PickerOption[] => {
        return generateOptionsString(
            t,
            ['created', 'booking_done', 'cancelled_no_charge', 'cancelled_charge'],
            'reservation:status.',
        );
    };

    // Usage for reservation status options
    const generateReservationStatusOptions = (): PickerOption[] => {
        return generateOptionsString(
            t,
            ['created', 'booking_done', 'splitted', 'cancelled_no_charge', 'cancelled_charge'],
            'reservation:status.',
        );
    };

    //=======================================//
    //=============== Actions ===============//
    //=======================================//

    const handleCancelReservationAction = (obj: any) => () => {
        // props.cancelReservation(obj.id, { description: 'Call Center - Manual Cancel' }, () => {
        //     if (tableRef?.current) {
        //         tableRef.current.reload();
        //     }
        // });
        props.cancelReservationCallCenter({ uuid: obj.uuid, phonenumber: foundRider.phone_number }, () => {
            if (tableRef?.current) {
                tableRef.current.reload();
            }
        });
    };

    const handleCancelBookingAction = (obj: any) => () => {
        props.cancelBookingCallCenter({ uuid: obj.uuid, phonenumber: foundRider.phone_number }, () => {
            if (tableBookingRef?.current) {
                tableBookingRef.current.reload();
            }
        });
    };

    const handleImpersonate = () => {
        impersonateUser(`${foundRider.uuid}`, () => {
            UserUtils.setImpersonateInfos(window.location.href, foundRider);
            window.location.href = `${envConfig.webapp_rider_url}`;
        });
    };

    //========================================//
    //=============== Builders ===============//
    //========================================//

    const headerBookingBuilder = (): HeadCell[] => {
        const headers: HeadCell[] = [
            //  createCell(t, Cell.ID, { sortable: true }),
            {
                id: 'id',
                label: t('common:form.id'),
                contains: false,
                filterable: false,
                sortable: false,
                formatCell: function (row) {
                    return <div>{row.number ?? ''}</div>;
                },
            },
            {
                id: 'rider.fullname',
                label: t('booking:rider'),
                formatCell: function (row) {
                    return (
                        <>
                            <div style={{ fontWeight: 600 }}>{row.rider.fullname}</div>
                        </>
                    );
                },
            },
            { id: 'driver.fullname', label: t('booking:driver') },
            {
                id: 'status',
                label: t('booking:status'),
                type: FormatType.Picker,
                pickerOptions: generateBookingStatusOptions(),
                formatCell: function (row) {
                    let translation = t('booking:booking_status_' + row.status);
                    if (row.cancellation_reason) {
                        translation += ' - ' + t('booking:' + row.cancellation_reason);
                    }
                    return translation;
                },
            },
            {
                id: 'creation_date',
                label: t('booking:creation_date'),
                type: FormatType.DateLong,
                filterable: false,
            },
            {
                id: 'pickup_location',
                label: t('booking:pickup_location'),
                type: FormatType.Address,
                filterable: false,
                sortable: false,
            },
            {
                id: 'planned_dropoff_location',
                label: t('booking:planned_dropoff_location'),
                type: FormatType.Address,
                filterable: false,
                sortable: false,
            },
            {
                id: 'dispatch_group.id',
                label: t('booking:dispatch_group'),
                filterable: dispatchGroups.length > 0,
                type: FormatType.Picker,
                pickerOptions: getDispatchGroupOptions(dispatchGroups),
                formatCell: function (row) {
                    return `${row.dispatch_group?.name ?? ''}`;
                },
            },
            {
                id: 'action',
                label: t('common:action'),
                filterable: false,
                sortable: false,
                noLink: true,
                formatCell: (row) => {
                    return (
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            {UserUtils.checkRights(AccountRight.RIDE_ACTION_CANCEL) &&
                                (row.status === BookingStatus.CREATED ||
                                    row.status === BookingStatus.DRIVER_ARRIVED ||
                                    row.status === BookingStatus.ACCEPTED) && (
                                    <Dialog
                                        btnTitle={t('common:validate')}
                                        description={t('common:cancelParam', { id: row.id })}
                                        customButton={
                                            <TooltipButton title={t('common:delete')}>
                                                <Button
                                                    size="small"
                                                    variant="contained"
                                                    color="primary"
                                                    style={{ padding: 6 }}
                                                    className={clsx(
                                                        globalClasses.btnAutoWidth,
                                                        globalClasses.btnDanger,
                                                    )}
                                                >
                                                    <Icon fontSize="small">delete_forever</Icon>
                                                </Button>
                                            </TooltipButton>
                                        }
                                        onOK={handleCancelBookingAction(row)}
                                    />
                                )}
                        </div>
                    );
                },
            },
        ];
        return headers;
    };

    const headerReservationBuilder = (): HeadCell[] => {
        const headers: HeadCell[] = [
            {
                id: 'id',
                label: t('common:form.id'),
                contains: false,
                filterable: false,
                sortable: false,
                formatCell: function (row) {
                    return <div>{row.number ?? ''}</div>;
                },
            },
            {
                id: 'rider.fullname',
                label: t('reservation:rider'),
                formatCell: function (row) {
                    return (
                        <>
                            <div style={{ fontWeight: 600 }}>{row.rider.fullname}</div>
                        </>
                    );
                },
            },
            {
                id: 'status',
                label: t('reservation:status.title'),
                type: FormatType.Picker,
                pickerOptions: generateReservationStatusOptions(),
                formatCell: function (row) {
                    const translation = t('reservation:status.' + row.status);
                    return translation;
                },
            },
            {
                id: 'planned_start_date',
                label: t('reservation:planned_start_date'),
                type: FormatType.DateLong,
                filterable: false,
            },
            {
                id: 'planned_start_location',
                label: t('reservation:planned_start_location'),
                type: FormatType.Address,
                filterable: false,
                sortable: false,
            },
            {
                id: 'planned_end_location',
                label: t('reservation:planned_end_location'),
                type: FormatType.Address,
                filterable: false,
                sortable: false,
            },
            {
                id: 'dispatch_group',
                label: t('reservation:dispatch_group'),
                filterable: dispatchGroups.length > 0,
                type: FormatType.Picker,
                pickerOptions: getDispatchGroupOptions(dispatchGroups),
                formatCell: function (row) {
                    return `${row.dispatch_group?.name ?? ''}`;
                },
            },
            {
                id: 'action',
                label: t('common:action'),
                filterable: false,
                sortable: false,
                noLink: true,
                formatCell: (row) => {
                    return (
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            {UserUtils.checkRight(AccountRight.RIDE_DETAILS, RightRank.W) &&
                                row.status === ReservationStatus.CREATED && (
                                    <TooltipButton title={t('common:edit')}>
                                        <Button
                                            size="small"
                                            variant="contained"
                                            color="secondary"
                                            style={{ marginRight: 8, padding: 6 }}
                                            component={RouterLink}
                                            to={`${paths.reservations}/${row.id}/edit`}
                                            className={globalClasses.btnAutoWidth}
                                        >
                                            <Icon fontSize="small">edit</Icon>
                                        </Button>
                                    </TooltipButton>
                                )}
                            {UserUtils.checkRights(AccountRight.RIDE_ACTION_CANCEL) &&
                                (row.status === ReservationStatus.CREATED ||
                                    row.status === ReservationStatus.ALLOCATED) && (
                                    <Dialog
                                        btnTitle={t('common:validate')}
                                        description={t('common:cancelParam', { id: row.id })}
                                        customButton={
                                            <TooltipButton title={t('common:delete')}>
                                                <Button
                                                    size="small"
                                                    variant="contained"
                                                    color="primary"
                                                    style={{ padding: 6 }}
                                                    className={clsx(
                                                        globalClasses.btnAutoWidth,
                                                        globalClasses.btnDanger,
                                                    )}
                                                >
                                                    <Icon fontSize="small">delete_forever</Icon>
                                                </Button>
                                            </TooltipButton>
                                        }
                                        onOK={handleCancelReservationAction(row)}
                                    />
                                )}
                        </div>
                    );
                },
            },
        ];
        return headers;
    };

    const buttonCreate = (
        <>
            <Button
                size="small"
                variant="contained"
                color="secondary"
                onClick={handleImpersonate}
                className={classes.roundButton}
                startIcon={<Icon>person</Icon>}
            >
                {t('impersonate')}
            </Button>
            <ButtonTo
                text={t('create')}
                to={`${foundRider.id}/add`}
                rights={AccountRight.RIDE_ACTION_CREATE}
                startIcon={<Icon>event_available</Icon>}
            />
        </>
    );

    return (
        <App
            noFav
            header={{
                title: t('description'),
            }}
        >
            {foundRider?.id ? (
                <>
                    <div className={clsx(globalClasses.block, classes.flex)}>
                        <p className={globalClasses.details}>
                            {t('user', {
                                fullname: StringUtils.userFullname(foundRider),
                            })}
                            {foundRider.isModeCB && t('user_cb', { cb: foundRider.cb })}
                        </p>
                    </div>

                    <ErrorMessage payload={props.impersonateUserError} />
                    <ErrorMessage payload={cancelReservationError || cancelReservationCallCenterError} />
                    <EnhancedTable
                        onRef={(ref) => (tableBookingRef.current = ref)}
                        title={t('booking:title')}
                        headers={headerBookingBuilder()}
                        type={tableRequestPoints.BOOKINGS}
                        filterParams={{ 'filters[client.id]': foundRider.id }}
                        filterVisible={false}
                        actions={buttonCreate}
                    />

                    <ErrorMessage payload={cancelBookingCallCenterError} />
                    <EnhancedTable
                        onRef={(ref) => (tableRef.current = ref)}
                        title={t('reservation:title')}
                        headers={headerReservationBuilder()}
                        type={tableRequestPoints.RESERVATIONS}
                        filterParams={{ 'filters[client.id]': foundRider.id }}
                        filterVisible={false}
                        actions={buttonCreate}
                    />
                </>
            ) : (
                <ReserverForbiddenView />
            )}
        </App>
    );
};

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        blockMargin: { marginRight: 40 },
        flex: { flex: 1 },
        buttons: { display: 'flex', justifyContent: 'flex-end' },
        roundButton: {
            fontSize: 15,
            marginRight: 8,
            padding: '12px 20px',
            alignSelf: 'flex-end',
            borderRadius: 23,
            textTransform: 'none',
        },
    }),
);

const ReserverViewPageWithTranslation = withGlobalStyles(
    withTranslation(['reserver', 'booking', 'reservation', 'common'])(ReserverViewPage),
);

const mapStateToProps = (state) => ({
    foundRider: riderSelectors.foundRider(state),
    dispatchGroups: driverSelectors.sortedDispatchGroup(state),
    cancelReservationError: errorSelectors.cancelReservationError(state),
    cancelReservationCallCenterError: errorSelectors.cancelReservationCallCenterError(state),
    cancelBookingCallCenterError: errorSelectors.cancelBookingCallCenterError(state),
    impersonateUserError: errorSelectors.impersonateUserError(state),
});

const mapDispatchToProps = {
    getDispatchGroups: driverActions.getDispatchGroups,
    cancelReservation: reservationActions.cancelReservation,
    cancelReservationCallCenter: reservationActions.cancelReservationCallCenter,
    cancelBookingCallCenter: bookingActions.cancelBookingCallCenter,
    impersonateUser: riderActions.impersonateUser,
};

export default connect(mapStateToProps, mapDispatchToProps)(ReserverViewPageWithTranslation);
